import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { CustomStatusPanelProps } from 'ag-grid-react';

import { formatAmount } from '@/components/AgGrid/helpers/numberFormat';
import { useGridEventListener } from '@/components/AgGrid/hooks/useGridEventListener';
import type { BankFeedTransactionRow } from '@/domain/bankFeed/schemas';
import {
  computeSelectionSummary,
  type SelectionSummary,
} from '@/pages/company/BankFeedRoute/components/BankFeedGrid/BankFeedSumStatusPanel/computeSelectionSummary';

// Status panel showing the sum of drag-selected amount cells — Workspace grid's
// AmountSumStatusPanelRenderer re-authored for the bank-feed grid (DA-19100):
// typed panel props, plain float-string parsing, listener cleanup on unmount.
const BankFeedSumStatusPanel: React.FC<CustomStatusPanelProps<BankFeedTransactionRow>> = ({ api }) => {
  const { formatMessage } = useIntl();
  const [{ sum, count }, setSummary] = useState<SelectionSummary>({ sum: 0, count: 0 });

  const handleCellSelectionChanged = useCallback(() => setSummary(computeSelectionSummary(api)), [api]);
  useGridEventListener(api, 'cellSelectionChanged', handleCellSelectionChanged);

  const hasSummary = count > 1;

  return (
    // AG Grid's own status-bar CSS classes — native look, themed via useAgGridTheme.
    <div
      className="ag-status-name-value"
      data-visible={hasSummary || undefined}
      style={{ display: hasSummary ? 'flex' : 'none' }}
    >
      <span className="component">
        {formatMessage({ id: 'company.workspace.grid.statusBar.sum', defaultMessage: 'Sum' })}:&nbsp;
      </span>
      <span className="ag-status-name-value-value" style={{ direction: 'ltr' }}>
        {formatAmount(sum)}
      </span>
    </div>
  );
};

export default BankFeedSumStatusPanel;
